export interface WonderCharacterDefinition {
	name: string;
	subjectArea?: string;
	mainMascot?: boolean;
}

export const WONDER_CHARACTER_DEFINITIONS = {
	parrot: {
		name: 'Archie',
		mainMascot: true,
	},
	bee: {
		name: 'Buzz',
	},
	fox: {
		name: 'Fable',
		subjectArea: 'reading',
	},
	frog: {
		name: 'Splash',
		subjectArea: 'art',
	},
	otter: {
		name: 'Eddy',
		subjectArea: 'science',
	},
	owl: {
		name: 'Abby',
		subjectArea: 'math',
	},
	rabbit: {
		name: 'Clover',
		subjectArea: 'social studies',
	},
	turtle: {
		name: 'Tempo',
		subjectArea: 'wellness',
	},
} as const satisfies Record<string, WonderCharacterDefinition>;

export type WonderCharacterKey = keyof typeof WONDER_CHARACTER_DEFINITIONS;
export type WonderCharacterImageType = 'default' | 'default:shadow';
export type WonderCharacterSubjectKey =
	| 'art'
	| 'english'
	| 'health'
	| 'math'
	| 'science'
	| 'social';

export const WONDER_DEFAULT_CHARACTER: WonderCharacterKey = 'parrot';

const WONDER_CHARACTER_SUBJECT_AREAS = {
	art: 'art',
	english: 'reading',
	health: 'wellness',
	math: 'math',
	science: 'science',
	social: 'social studies',
} as const satisfies Record<WonderCharacterSubjectKey, string>;

export function getWonderCharacterForSubjectArea(
	subjectArea: string,
): WonderCharacterKey {
	const characters = getWonderCharactersForSubjectArea(subjectArea);

	return (
		characters.find(
			(character) => !getWonderCharacterDefinition(character).mainMascot,
		) ??
		characters[0] ??
		WONDER_DEFAULT_CHARACTER
	);
}

export function getWonderCharactersForSubjectArea(
	subjectArea: string,
): WonderCharacterKey[] {
	const normalizedSubjectArea = subjectArea.trim().toLowerCase();

	return getWonderCharacters().filter(
		(character) =>
			getWonderCharacterDefinition(character).subjectArea?.toLowerCase() ===
			normalizedSubjectArea,
	);
}

export function getWonderCharacters(): WonderCharacterKey[] {
	return Object.keys(WONDER_CHARACTER_DEFINITIONS) as WonderCharacterKey[];
}

export function getWonderCharacterDefinition(
	character: WonderCharacterKey,
): WonderCharacterDefinition {
	return WONDER_CHARACTER_DEFINITIONS[character];
}

export function getWonderCharacterForSubjectKey(
	subjectKey: WonderCharacterSubjectKey | null | undefined,
): WonderCharacterKey {
	return subjectKey == null
		? WONDER_DEFAULT_CHARACTER
		: getWonderCharacterForSubjectArea(
				WONDER_CHARACTER_SUBJECT_AREAS[subjectKey],
			);
}

export function getWonderSubjectKeyForCharacter(
	character: WonderCharacterKey | null | undefined,
): WonderCharacterSubjectKey | null {
	if (character == null) {
		return null;
	}

	const subjectArea = getWonderCharacterDefinition(character).subjectArea;

	if (subjectArea == null) {
		return null;
	}

	for (const subjectKey of Object.keys(
		WONDER_CHARACTER_SUBJECT_AREAS,
	) as WonderCharacterSubjectKey[]) {
		if (WONDER_CHARACTER_SUBJECT_AREAS[subjectKey] === subjectArea) {
			return subjectKey;
		}
	}

	return null;
}

export function getWonderCharacterImagePath(
	character: WonderCharacterKey,
	type: WonderCharacterImageType = 'default',
): string {
	const name = WONDER_CHARACTER_DEFINITIONS[character].name.toLowerCase();

	if (type === 'default:shadow') {
		return `/wonder/characters/${name}-${character}_shadow.png`;
	}

	return `/wonder/characters/${name}-${character}.png`;
}
